// ABOUTME: Custom hook for handling navigation tab overflow calculations
// ABOUTME: Measures real rendered tab widths and recalculates on resize for responsive tab management
import { useEffect, useState, useCallback, useRef } from "react"

import { NavigationTabsProps } from "."

interface UseTabOverflowProps {
	navigationTabs: NavigationTabsProps[] | undefined
	containerRef: React.RefObject<HTMLDivElement>
	tabsPanelRef: React.RefObject<HTMLDivElement>
	leftButtonsRef?: React.RefObject<HTMLDivElement>
	rightButtonsRef?: React.RefObject<HTMLDivElement>
	logoRef?: React.RefObject<HTMLDivElement>
}

interface TabOverflowResult {
	visibleTabs: NavigationTabsProps[]
	overflowTabs: NavigationTabsProps[]
	shouldShowMore: boolean
}

const CONTAINER_PADDING = 96
const MORE_BUTTON_WIDTH = 64
const DEBOUNCE_DELAY = 150

export const useTabOverflow = ({
	navigationTabs,
	containerRef,
	tabsPanelRef,
	leftButtonsRef,
	rightButtonsRef,
	logoRef,
}: UseTabOverflowProps): TabOverflowResult => {
	const [visibleTabs, setVisibleTabs] = useState<NavigationTabsProps[]>([])
	const [overflowTabs, setOverflowTabs] = useState<NavigationTabsProps[]>([])
	const [shouldShowMore, setShouldShowMore] = useState(false)
	const resizeTimerRef = useRef<ReturnType<typeof setTimeout>>()

	const measureTabWidths = useCallback((): number[] | null => {
		if (!tabsPanelRef.current) return null

		const tabElements =
			tabsPanelRef.current.querySelectorAll<HTMLElement>(
				"[data-tab-item]",
			)
		if (tabElements.length === 0) return null

		return Array.from(tabElements).map(
			el => el.getBoundingClientRect().width,
		)
	}, [tabsPanelRef])

	const getAvailableWidth = useCallback(() => {
		if (!containerRef.current) return null

		const containerWidth = containerRef.current.offsetWidth

		const leftButtonsWidth = leftButtonsRef?.current
			? leftButtonsRef.current.getBoundingClientRect().width + 12
			: 0
		const rightButtonsWidth = rightButtonsRef?.current
			? rightButtonsRef.current.getBoundingClientRect().width + 48
			: 0
		const logoWidth = logoRef?.current
			? logoRef.current.getBoundingClientRect().width + 32
			: 0

		return (
			containerWidth -
			leftButtonsWidth -
			rightButtonsWidth -
			logoWidth -
			CONTAINER_PADDING
		)
	}, [containerRef, leftButtonsRef, rightButtonsRef, logoRef])

	const calculateOverflow = useCallback(() => {
		if (
			!navigationTabs?.length ||
			!containerRef.current ||
			!tabsPanelRef.current
		) {
			return
		}

		const availableWidth = getAvailableWidth()
		if (availableWidth === null) return

		const tabWidths = measureTabWidths()
		if (tabWidths?.length !== navigationTabs.length) return

		const gap = 8
		const gapsWidth = Math.max(0, (navigationTabs.length - 1) * gap)
		const totalTabsWidth =
			tabWidths.reduce((sum, w) => sum + w, 0) + gapsWidth

		if (totalTabsWidth <= availableWidth) {
			setVisibleTabs(navigationTabs)
			setOverflowTabs([])
			setShouldShowMore(false)
			return
		}

		const availableForTabs = availableWidth - MORE_BUTTON_WIDTH
		let usedWidth = 0
		let visibleCount = 0

		for (let i = 0; i < tabWidths.length; i++) {
			const gapWidth = i > 0 ? gap : 0
			const totalWidthWithTab = usedWidth + tabWidths[i] + gapWidth

			if (totalWidthWithTab <= availableForTabs) {
				usedWidth = totalWidthWithTab
				visibleCount = i + 1
			} else {
				break
			}
		}

		visibleCount = Math.max(1, visibleCount)

		setVisibleTabs(navigationTabs.slice(0, visibleCount))
		setOverflowTabs(navigationTabs.slice(visibleCount))
		setShouldShowMore(visibleCount < navigationTabs.length)
	}, [
		navigationTabs,
		measureTabWidths,
		getAvailableWidth,
		containerRef,
		tabsPanelRef,
	])

	const debouncedCalculate = useCallback(() => {
		clearTimeout(resizeTimerRef.current)
		resizeTimerRef.current = globalThis.setTimeout(
			calculateOverflow,
			DEBOUNCE_DELAY,
		)
	}, [calculateOverflow])

	// Initial calculation: show all tabs first so they render for measurement,
	// then calculate overflow on the next frame.
	useEffect(() => {
		if (!navigationTabs?.length) {
			setVisibleTabs([])
			setOverflowTabs([])
			setShouldShowMore(false)
			return
		}

		setVisibleTabs(navigationTabs)
		setOverflowTabs([])
		setShouldShowMore(false)

		const frameId = requestAnimationFrame(() => {
			calculateOverflow()
		})
		return () => cancelAnimationFrame(frameId)
	}, [navigationTabs, calculateOverflow])

	useEffect(() => {
		globalThis.addEventListener("resize", debouncedCalculate)
		return () => {
			globalThis.removeEventListener("resize", debouncedCalculate)
			clearTimeout(resizeTimerRef.current)
		}
	}, [debouncedCalculate])

	return {
		visibleTabs,
		overflowTabs,
		shouldShowMore,
	}
}
